import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import { VoiceAI } from "../../types/types";
import {
    activateVoiceAdmin,
    deactivateVoiceAdmin,
    deactivateVoices,
    DisabledVoice,
    getVoicesAdmin,
    refreshVoicesAdmin,
    removeStaleVoices,
    StaleVoice,
} from "../../api/administration";
import { useLanguage } from "../../context/useLanguage";
import TextInput from "../../components/ui/TextInput";
import Button from "../../components/ui/Button";
import ToggleSwitch from "../../components/common/ToggleSwitch";
import Icon from "../../components/common/Icon";
import logo from "../../assets/images/logo-rewind.png";
import studioModal from "../../components/modals/StudioModal.module.css";

const StaleVoicesModal: React.FC<{
    isOpen: boolean;
    staleVoices: StaleVoice[];
    onConfirm: (selectedIds: string[]) => void;
    onClose: () => void;
}> = ({ isOpen, staleVoices, onConfirm, onClose }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [confirmationText, setConfirmationText] = useState("");

    const requiredText = String(t("delete")).toLowerCase();
    const isConfirmed = confirmationText.toLowerCase() === requiredText;

    if (!isOpen || staleVoices.length === 0) return null;

    const toggleVoice = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === staleVoices.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(staleVoices.map((v) => v.id)));
        }
    };

    const modal = (
        <div className={studioModal.overlay}>
            <div className={`${studioModal.wideContent} ${studioModal.deleteContent}`} style={{ width: "90vw" }}>
                <div className={studioModal.logoWrap}>
                    <img src={logo} alt="Logo" />
                </div>
                <h1 className={studioModal.title}>{t("staleVoicesTitle")}</h1>
                <p style={{ textAlign: "center", marginBottom: "8px", color: "#666" }}>{t("staleVoicesDescription")}</p>
                <p style={{ textAlign: "center", marginBottom: "16px", color: "#e74c3c", fontWeight: 500 }}>
                    {t("staleVoicesWarning")}
                </p>

                <div style={{ maxHeight: "300px", overflowY: "auto", width: "100%" }}>
                    <table className={adminSurface.table} style={{ fontSize: "14px" }}>
                        <thead>
                            <tr>
                                <th style={{ width: "40px" }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.size === staleVoices.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th>{t("voiceName")}</th>
                                <th>{t("voiceLanguage")}</th>
                                <th>{t("status")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {staleVoices.map((voice) => (
                                <tr key={voice.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(voice.id)}
                                            onChange={() => toggleVoice(voice.id)}
                                        />
                                    </td>
                                    <td>{voice.name}</td>
                                    <td>{voice.language}</td>
                                    <td>{voice.isActive ? t("active") || "Active" : t("inactive") || "Inactive"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="delete-modal-confirmation-input" style={{ marginTop: "16px" }}>
                    <p className="delete-modal-confirmation-instruction">
                        {t("typeToConfirm")} <strong>"{requiredText}"</strong>
                    </p>
                    <TextInput
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        placeholder={requiredText}
                        customClassName="delete-modal-text-input"
                    />
                </div>

                <div className={studioModal.buttonsCentered} style={{ marginTop: "20px" }}>
                    <Button variant="cancel" label={t("cancel")} onClick={onClose} />
                    <Button
                        variant="danger"
                        shape="pill"
                        label={`${t("delete")} (${selectedIds.size})`}
                        onClick={() => onConfirm(Array.from(selectedIds))}
                        disabled={selectedIds.size === 0 || !isConfirmed}
                    />
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

const DisabledVoicesModal: React.FC<{
    isOpen: boolean;
    disabledVoices: DisabledVoice[];
    onConfirm: (selectedIds: string[]) => void;
    onClose: () => void;
}> = ({ isOpen, disabledVoices, onConfirm, onClose }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (isOpen) {
            setSelectedIds(new Set(disabledVoices.map((v) => v.id)));
        }
    }, [isOpen, disabledVoices]);

    if (!isOpen || disabledVoices.length === 0) return null;

    const toggleVoice = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedIds.size === disabledVoices.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(disabledVoices.map((v) => v.id)));
        }
    };

    const modal = (
        <div className={studioModal.overlay}>
            <div className={`${studioModal.wideContent} ${studioModal.deleteContent}`} style={{ width: "90vw" }}>
                <div className={studioModal.logoWrap}>
                    <img src={logo} alt="Logo" />
                </div>
                <h1 className={studioModal.title}>{t("disabledVoicesTitle") || "Voix désactivées par ElevenLabs"}</h1>
                <p style={{ textAlign: "center", marginBottom: "8px", color: "#666" }}>
                    {t("disabledVoicesDescription") ||
                        "Ces voix sont encore listées par ElevenLabs mais ne sont plus utilisables pour la génération (désactivées par leur propriétaire)."}
                </p>
                <p style={{ textAlign: "center", marginBottom: "16px", color: "#e67e22", fontWeight: 500 }}>
                    {t("disabledVoicesHint") ||
                        "Toutes sont présélectionnées. Décoche celles que tu souhaites garder actives malgré tout."}
                </p>

                <div style={{ maxHeight: "300px", overflowY: "auto", width: "100%" }}>
                    <table className={adminSurface.table} style={{ fontSize: "14px" }}>
                        <thead>
                            <tr>
                                <th style={{ width: "40px" }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.size === disabledVoices.length}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th>{t("voiceName")}</th>
                                <th>{t("voiceLanguage")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {disabledVoices.map((voice) => (
                                <tr key={voice.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(voice.id)}
                                            onChange={() => toggleVoice(voice.id)}
                                        />
                                    </td>
                                    <td>{voice.name}</td>
                                    <td>{voice.language}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className={studioModal.buttonsCentered} style={{ marginTop: "20px" }}>
                    <Button variant="cancel" label={t("cancel")} onClick={onClose} />
                    <Button
                        variant="danger"
                        shape="pill"
                        label={`${t("confirmDeactivation") || "Confirmer la désactivation"} (${selectedIds.size})`}
                        onClick={() => onConfirm(Array.from(selectedIds))}
                        disabled={selectedIds.size === 0}
                    />
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

const VoicesAdmin: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [voices, setVoices] = useState<VoiceAI[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshing, setRefreshing] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");
    const [staleVoices, setStaleVoices] = useState<StaleVoice[]>([]);
    const [showStaleModal, setShowStaleModal] = useState(false);
    const [disabledVoicesList, setDisabledVoicesList] = useState<DisabledVoice[]>([]);
    const [showDisabledModal, setShowDisabledModal] = useState(false);
    const { language } = useLanguage();

    useEffect(() => {
        fetchVoices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    const fetchVoices = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getVoicesAdmin(token, language);
            setVoices(response);
        } catch (error) {
            console.error("Failed to fetch voices", error);
        } finally {
            setLoading(false);
        }
    };

    const filteredVoices = voices
        .filter((voice) => voice.name?.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0));

    const activeVoiceCount = voices.filter((voice) => voice.isActive).length;

    if (loading) return <div>Chargement...</div>;

    const handleToggleStatus = async (voice: VoiceAI) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }

            if (voice.isActive) {
                await deactivateVoiceAdmin({
                    token,
                    voiceId: voice.id,
                });
            } else {
                await activateVoiceAdmin({
                    token,
                    voiceId: voice.id,
                });
            }
        } catch (error) {
            console.error("Failed to update voice status", error);
        } finally {
            await fetchVoices();
        }
    };

    const handleRefreshVoices = async () => {
        setRefreshing(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await refreshVoicesAdmin(token);
            await fetchVoices();

            // Queue both lists so we can chain modals: stale first, disabled after.
            // Showing them simultaneously would stack two overlays and let clicks
            // hit the wrong modal.
            const hasStale = response.staleVoices && response.staleVoices.length > 0;
            const hasDisabled = response.disabledVoices && response.disabledVoices.length > 0;

            if (hasDisabled) {
                setDisabledVoicesList(response.disabledVoices);
            }

            if (hasStale) {
                setStaleVoices(response.staleVoices);
                setShowStaleModal(true);
            } else if (hasDisabled) {
                setShowDisabledModal(true);
            }
        } catch (error) {
            console.error("Failed to refresh voices", error);
        } finally {
            setRefreshing(false);
        }
    };

    const openDisabledIfPending = () => {
        if (disabledVoicesList.length > 0) {
            setShowDisabledModal(true);
        }
    };

    const handleConfirmRemoveStale = async (selectedIds: string[]) => {
        setShowStaleModal(false);
        if (selectedIds.length === 0) {
            openDisabledIfPending();
            return;
        }

        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            await removeStaleVoices(token, selectedIds);
            await fetchVoices();
        } catch (error) {
            console.error("Failed to remove stale voices", error);
        } finally {
            openDisabledIfPending();
        }
    };

    const handleConfirmDeactivate = async (selectedIds: string[]) => {
        setShowDisabledModal(false);
        setDisabledVoicesList([]);
        if (selectedIds.length === 0) return;

        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            await deactivateVoices(token, selectedIds);
            await fetchVoices();
        } catch (error) {
            console.error("Failed to deactivate voices", error);
        }
    };

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("voices")} subtitle={t("voicesSubTitle")} />
            </div>

            <div className={adminSurface.tblBar}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchvoice")}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
                <button
                    type="button"
                    className={`${adminSurface.tblBarGhost} ${adminSurface.tblBarEnd}`}
                    onClick={handleRefreshVoices}
                    disabled={refreshing}
                >
                    <Icon name="ic-refresh" className="ic" size={18} />
                    {refreshing ? t("refreshing") || "Refreshing..." : t("refreshVoices")}
                </button>
            </div>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>{t("voiceName")}</th>
                                <th>{t("voiceId")}</th>
                                <th>{t("voiceOrigin")}</th>
                                <th>{t("voiceLanguage")}</th>
                                <th>{t("linkedCompanies")}</th>
                                <th>{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVoices.map((voice) => (
                                <tr key={voice.id}>
                                    <td>{voice.name}</td>
                                    <td
                                        style={{
                                            fontSize: "12px",
                                            fontFamily: "monospace",
                                            maxWidth: "180px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                        title={voice.id}
                                    >
                                        {voice.id}
                                    </td>
                                    <td>
                                        <span className="admin-tag-pill">{voice.origin}</span>
                                    </td>
                                    <td>
                                        <span className="admin-tag-pill">{voice.language}</span>
                                    </td>
                                    <td>{(voice as any).company?.map((c: any) => c.name).join(", ") || "—"}</td>
                                    <td>
                                        <div
                                            className={`${adminSurface.adminToggle}${voice.isActive ? ` ${adminSurface.adminToggleOn}` : ""}`}
                                        >
                                            <ToggleSwitch
                                                checked={!!voice.isActive}
                                                onChange={() => handleToggleStatus(voice)}
                                            />
                                            <span>
                                                {voice.isActive ? t("active") || "Active" : t("inactive") || "Inactive"}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredVoices.length} {t("voices")?.toLowerCase?.() || "voix"} · {activeVoiceCount}{" "}
                        {t("active")?.toLowerCase?.() || "active"}
                    </span>
                </div>
            </div>

            <StaleVoicesModal
                isOpen={showStaleModal}
                staleVoices={staleVoices}
                onConfirm={handleConfirmRemoveStale}
                onClose={() => {
                    setShowStaleModal(false);
                    openDisabledIfPending();
                }}
            />

            <DisabledVoicesModal
                isOpen={showDisabledModal}
                disabledVoices={disabledVoicesList}
                onConfirm={handleConfirmDeactivate}
                onClose={() => {
                    setShowDisabledModal(false);
                    setDisabledVoicesList([]);
                }}
            />
        </div>
    );
};

export default VoicesAdmin;
